import { logger } from "@platform/browser-logger";
import { useCallback, useRef } from "react";

import {
  useReorderBenefitPlanGroups,
  useReorderBenefitPlansInGroup,
} from "../api";

import type { BenefitPlanGroup, RfpCoverage } from "@platform/plan-grid-core";

/**
 * Derives each group's new intra-group plan order from the flat `planOrder`, keeping
 * only groups whose plan sequence actually changed. Reordering plans within a group
 * leaves group order untouched, so this is what the group-reorder call misses.
 *
 * `planOrder` holds AG Grid colIds, which are plan *identifiers* (e.g. "DEN-1-2"), not
 * plan UUIDs — so plans are matched by identifier (falling back to id), and the emitted
 * `orderedIds` are the plan UUIDs the reorder endpoint expects.
 */
const changedGroupPlanOrders = (
  planOrder: string[],
  benefitPlanGroups: BenefitPlanGroup[],
): { groupId: string; orderedIds: string[] }[] => {
  const orderIndex = new Map(planOrder.map((key, index) => [key, index]));

  const positionOf = (plan: { id: string; identifier: string }): number =>
    orderIndex.get(plan.identifier) ?? orderIndex.get(plan.id) ?? -1;

  return benefitPlanGroups.flatMap((group) => {
    const { benefitPlans } = group;

    // Skip groups whose plans aren't all represented in the drag order — a partial
    // list would drop plans on the backend, which requires every plan in the group.
    if (benefitPlans.some((plan) => positionOf(plan) < 0)) {
      return [];
    }

    const currentIds = benefitPlans.map((plan) => plan.id);
    const nextIds = [...benefitPlans]
      .sort((a, b) => positionOf(a) - positionOf(b))
      .map((plan) => plan.id);

    const orderChanged = nextIds.some((id, index) => id !== currentIds[index]);

    return orderChanged ? [{ groupId: group.id, orderedIds: nextIds }] : [];
  });
};

const isUniqueGroup = (
  seenGroups: Set<string>,
  group: { id: string; benefitPlans: { id: string }[] } | undefined,
): group is { id: string; benefitPlans: { id: string }[] } => {
  if (!group || seenGroups.has(group.id)) {
    return false;
  }
  seenGroups.add(group.id);
  return true;
};

/**
 * Converts plan order to group order based on benefit plan groups
 * @param planOrder - Array of plan identifiers in desired order
 * @param benefitPlanGroups - Array of benefit plan groups
 * @returns Array of group IDs in order
 */
export const convertPlanOrderToGroupOrder = (
  planOrder: string[],
  benefitPlanGroups: { id: string; benefitPlans: { id: string }[] }[],
): { id: string }[] => {
  const seenGroups = new Set<string>();

  // Extract group IDs from plan order, maintaining order and removing duplicates
  return planOrder
    .map((planIdentifier) =>
      // Find the benefit plan group that contains this plan identifier
      benefitPlanGroups?.find((group) =>
        group.benefitPlans.some((plan) => plan.id === planIdentifier),
      ),
    )
    .filter((group) => isUniqueGroup(seenGroups, group))
    .map((group) => ({ id: group.id }));
};

// Holds a reorder request until it settles, so export can wait on it.
const trackPendingRequest = (
  pendingRequests: Set<Promise<unknown>>,
  request: Promise<unknown>,
): void => {
  pendingRequests.add(request);
  const untrack = (): void => {
    pendingRequests.delete(request);
  };
  request.then(untrack, untrack);
};

interface UseHandlePlanReorderArgs {
  benefitPlanGroups: BenefitPlanGroup[] | undefined;
  selectedCoverage: RfpCoverage | undefined;
  updateSelectedCoverage: (updatedCoverage?: RfpCoverage) => void;
  onPlanReorder?: (planOrder: string[]) => void;
  rfpId: string;
  triggerSave: VoidFunction;
}

export const useHandlePlanReorder = ({
  benefitPlanGroups,
  selectedCoverage,
  updateSelectedCoverage,
  onPlanReorder,
  rfpId,
  triggerSave,
}: UseHandlePlanReorderArgs): {
  handlePlanReorder: (planOrder: string[]) => void;
  /** Resolves once in-flight reorder requests settle; rejects if one failed. */
  waitForPendingReorders: () => Promise<void>;
} => {
  const pendingReordersRef = useRef(new Set<Promise<unknown>>());

  const reorderBenefitPlanGroupsMutation = useReorderBenefitPlanGroups({
    onSuccess: (response) => {
      logger.info(
        "[PlanDesigner] Successfully reordered benefit plan groups:",
        { response },
      );
    },
    onError: (error) => {
      logger.error(
        "[PlanDesigner] Error reordering benefit plan groups:",
        error,
      );
    },
  });

  const reorderBenefitPlansInGroupMutation = useReorderBenefitPlansInGroup({
    onError: (error) => {
      logger.error(
        "[PlanDesigner] Error reordering plans within group:",
        error,
      );
    },
  });

  // AG Grid captures its `onColumnMoved` handler (and therefore this callback) at
  // grid-mount time, when the plan data may still be loading. Reading live values
  // through a ref keeps `handlePlanReorder` stable while a drop — even one routed
  // through a stale-captured handler — always persists against the current groups
  // and coverage, rather than an empty snapshot from mount.
  const latestRef = useRef({
    benefitPlanGroups,
    selectedCoverage,
    updateSelectedCoverage,
    onPlanReorder,
    rfpId,
    triggerSave,
    reorderBenefitPlanGroupsMutation,
    reorderBenefitPlansInGroupMutation,
  });
  latestRef.current = {
    benefitPlanGroups,
    selectedCoverage,
    updateSelectedCoverage,
    onPlanReorder,
    rfpId,
    triggerSave,
    reorderBenefitPlanGroupsMutation,
    reorderBenefitPlansInGroupMutation,
  };

  const handlePlanReorder = useCallback((planOrder: string[]): void => {
    const {
      benefitPlanGroups,
      selectedCoverage,
      updateSelectedCoverage,
      onPlanReorder,
      rfpId,
      triggerSave,
      reorderBenefitPlanGroupsMutation,
      reorderBenefitPlansInGroupMutation,
    } = latestRef.current;

    if (!selectedCoverage) {
      logger.warn("[PlanDesigner] Cannot reorder plans - no coverage selected");
      return;
    }

    logger.info("[PlanDesigner] Reordering plans:", {
      planOrder,
      currentCoverage: selectedCoverage.type,
    });

    updateSelectedCoverage({ ...selectedCoverage, columnOrder: planOrder });
    onPlanReorder?.(planOrder);

    if (benefitPlanGroups?.length) {
      const groupOrder = convertPlanOrderToGroupOrder(
        planOrder,
        benefitPlanGroups,
      );

      // Only reorder groups when the group order actually changed. Firing this
      // non-optimistic mutation on a pure intra-group move would invalidate the
      // benefit-plan-group cache mid-flight and transiently clobber the optimistic
      // plan reorder (a visible snap-back before it settles).
      const currentGroupOrder = benefitPlanGroups.map((group) => group.id);
      const groupOrderChanged = groupOrder.some(
        (group, index) => group.id !== currentGroupOrder[index],
      );

      if (groupOrder.length && groupOrderChanged) {
        trackPendingRequest(
          pendingReordersRef.current,
          reorderBenefitPlanGroupsMutation.mutateAsync({
            rfpId,
            coverageType: selectedCoverage.type,
            productType: benefitPlanGroups[0].productType,
            benefitPlanGroups: groupOrder,
          }),
        );
      }

      // Persist intra-group plan order so the new order — and the plan
      // identifiers derived from it — survive a refetch.
      for (const { groupId, orderedIds } of changedGroupPlanOrders(
        planOrder,
        benefitPlanGroups,
      )) {
        trackPendingRequest(
          pendingReordersRef.current,
          reorderBenefitPlansInGroupMutation.mutateAsync({
            rfpId,
            groupId,
            orderedIds,
          }),
        );
      }
    }

    triggerSave();
  }, []);

  const waitForPendingReorders = useCallback(async (): Promise<void> => {
    await Promise.all(pendingReordersRef.current);
  }, []);

  return { handlePlanReorder, waitForPendingReorders };
};
